import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Building2, Sparkles, Loader2, Plus } from "lucide-react";
import { BidRow } from "@/components/BidRow";
import api from "@/lib/api";
import { toast } from "sonner";

const inputCls =
  "focus-visible:ring-2 focus-visible:ring-[#F97316]/30 focus-visible:border-[#F97316]";

export const newBid = () => ({
  id: Math.random().toString(36).slice(2),
  carrierId: "",
  carrierName: "",
  rate: "",
});

const MAX_BIDS = 3;

export default function CarrierBids({
  carriers,
  bids,
  setBids,
  marginPct,
  setMarginPct,
  pickup,
  destination,
  units,
}) {
  const [rationale, setRationale] = useState("");
  const [recLoading, setRecLoading] = useState(false);

  const validBids = bids.filter((b) => Number(b.rate) > 0);
  const lowestId = validBids.length
    ? validBids.reduce((m, b) => (Number(b.rate) < Number(m.rate) ? b : m)).id
    : null;

  const updBid = (id, patch) =>
    setBids(bids.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const removeBid = (id) => {
    const next = bids.filter((b) => b.id !== id);
    setBids(next.length ? next : [newBid()]);
  };

  const recommend = async () => {
    setRecLoading(true);
    setRationale("");
    try {
      const { data } = await api.post("/ai/recommend-carrier", {
        pickup,
        destination: destination || "the delivery destination",
        units,
      });
      setRationale(data.rationale);
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI recommendation failed");
    } finally {
      setRecLoading(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-lg shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-[#F97316]" />
          <h3 className="text-sm font-bold uppercase tracking-[0.1em] text-[#1C1917]">
            Carrier Bids
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={recommend}
          disabled={recLoading}
          className="border-[#F97316]/40 text-[#EA580C] hover:bg-[#FFF7ED]"
          data-testid="ai-recommend-carrier-button"
        >
          {recLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
          AI Recommend
        </Button>
      </div>

      {rationale && (
        <div className="rounded-md bg-[#FFF7ED] border border-[#F97316]/30 p-3 text-sm text-[#1C1917] whitespace-pre-wrap" data-testid="ai-rationale">
          {rationale}
        </div>
      )}

      <p className="text-xs text-[#57534E]">
        Collect up to {MAX_BIDS} carrier quotes — the lowest bid feeds the three-way comparison.
      </p>

      <div className="space-y-3">
        {bids.map((b, i) => (
          <BidRow
            key={b.id}
            bid={b}
            index={i}
            carriers={carriers}
            isLowest={b.id === lowestId && validBids.length > 1}
            canRemove={bids.length > 1}
            onUpdate={(patch) => updBid(b.id, patch)}
            onRemove={() => removeBid(b.id)}
          />
        ))}
      </div>

      {bids.length < MAX_BIDS && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setBids([...bids, newBid()])}
          className="border-dashed w-full"
          data-testid="add-carrier-bid-button"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add carrier bid ({bids.length}/{MAX_BIDS})
        </Button>
      )}

      <div>
        <Label className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#57534E]">
          Margin % on winning bid (optional)
        </Label>
        <div className="relative mt-1">
          <Input
            type="number"
            value={marginPct}
            onChange={(e) => setMarginPct(e.target.value)}
            placeholder="0"
            className={`pr-8 ${inputCls}`}
            data-testid="bid-margin-input"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#57534E]">%</span>
        </div>
        <p className="text-[11px] text-[#57534E] mt-1">
          Leave blank to pass the bid through at cost.
        </p>
      </div>
    </div>
  );
}
